import React, { createContext, useContext, useState } from "react";
import { PusherService } from "./../services/notification/pusher";
import config from "../services/notification/config";
import { Events } from "src/services/notification/utils/types.ds";
import notificationChannels from "../services/notification/channels";
import { useAuth } from "./auth-context";

interface INotificationContext {
	notifications: INotification[];
  newNotification: INotification;
	setNotifications: React.Dispatch<React.SetStateAction<INotification[]>>;
  setNewNotification: React.Dispatch<React.SetStateAction<INotification>>;
	subscribeToChannel: (channel: INoteChannel) => void;
	unSubscribeToChannel: (channel: INoteChannel) => void;
}

interface INotification {
	id: string | number;
	title: string;
	description: string;
	time?: string;
	read?: boolean;
}

interface INoteChannel {
	channel: string;
	eventNames: string[];
}

const {
	pusher: { appKey, cluster },
} = config;
const notification = new PusherService({ appKey, cluster });

export const NotificationContext = createContext<
	INotificationContext | undefined
>(undefined);

const getSubscribePayload = ({
	eventChannel,
	setNotifications,
	setNewNotification,
	userEmail,
}: {
	eventChannel: INoteChannel;
	setNotifications: (prev) => void;
	setNewNotification: (val: INotification) => void;
	userEmail: string;
}) => {
	const channel = notificationChannels({
		setNotifications,
		userEmail,
		setNewNotification,
	}).find((noteChannel) => noteChannel.channel === eventChannel.channel);

	const channelEvents = eventChannel.eventNames.flatMap((ch) =>
		channel.channelEvents.filter((noteChannel) => noteChannel.eventName === ch),
	);
	return {
		channel: eventChannel.channel,
		channelEvents,
		eventChannel,
	} as unknown as Events;
};

export const NotificationProvider = ({ children }) => {
	const [notifications, setNotifications] = useState<INotification[]>([]);
	const [newNotification, setNewNotification] = useState<INotification>(null);

	const { appUser } = useAuth();
	const subscribeToChannel = (channel: INoteChannel) => {
		const userChannel = {
			...channel,
			channel: `${channel.channel}_${appUser.email}`,
		};

		const notificationChannelMap = getSubscribePayload({
			eventChannel: userChannel,
			setNotifications,
			setNewNotification,
			userEmail: appUser?.email,
		});
		notification.subscribeToChannels(notificationChannelMap);
	}; 

	const unSubscribeToChannel = (channel: INoteChannel) => {
		const userChannel = {
			...channel,
			channel: `${channel.channel}_${appUser.email}`,
		};
		const notificationChannelMap = getSubscribePayload({
			eventChannel: userChannel,
			setNotifications,
			setNewNotification,
			userEmail: appUser?.email,
		});
		notification.unSubscribeToChannels(notificationChannelMap);
	};

	const values = {
		notifications,
		setNotifications,
		subscribeToChannel,
		unSubscribeToChannel,
		newNotification,
		setNewNotification,
	};

	return (
		<NotificationContext.Provider value={values}>
			{children}
		</NotificationContext.Provider>
	);
};


export const useNotification = () => {
	const notificationContext = useContext(NotificationContext);
	if (notificationContext === undefined) {
		throw new Error(
			"useNotification must be withing the Notification Provider",
		);
	}

	return notificationContext;
};
